/**
 * Path autocomplete corpus + matching for Document Studio.
 *
 * Firestore's web SDK cannot enumerate collections, so collection/subcollection
 * name suggestions come from the app's canonical path registry (references.ts),
 * reflected here so they stay current. Document-id suggestions are fetched live
 * (see suggestDocumentIds in documentSource.ts). Parameterised registry entries
 * are surfaced with ":id" placeholders and only used to suggest their literal
 * (collection) segments.
 */

import {
  FirestoreCollectionReference,
  FirestoreDocumentReference,
} from "../../../../../../shared/api/firebase/references";
import { normalizePath } from "./documentSource";

type PathFactory = (...args: string[]) => string;

const PLACEHOLDERS = [":id", ":id2", ":id3"];

const templatesFrom = (registry: Record<string, PathFactory>): string[] => {
  const paths: string[] = [];
  for (const factory of Object.values(registry)) {
    try {
      const path = normalizePath(factory(...PLACEHOLDERS));
      if (path) {
        paths.push(path);
      }
    } catch {
      // Skip any entry that does not template cleanly.
    }
  }
  return paths;
};

/** Every canonical path template, de-duplicated and sorted. */
const CURATED_PATHS: string[] = Array.from(
  new Set([
    ...templatesFrom(
      FirestoreCollectionReference as Record<string, PathFactory>,
    ),
    ...templatesFrom(FirestoreDocumentReference as Record<string, PathFactory>),
  ]),
).sort();

/** Concrete (placeholder-free) document paths — directly openable quick-picks. */
const CURATED_DOCUMENTS: string[] = CURATED_PATHS.filter(
  (path) => !path.includes(":") && path.split("/").length % 2 === 0,
);

export interface EditingPath {
  /** Committed segments before the segment being typed, joined by "/". */
  base: string;
  /** The partial segment currently being typed. */
  fragment: string;
  /** Slot index of the fragment (0-based). Even = collection, odd = document. */
  index: number;
}

/** Split a raw input into its committed base and the segment being typed. */
export const splitEditingPath = (value: string): EditingPath => {
  const lastSlash = value.lastIndexOf("/");
  const rawBase = lastSlash === -1 ? "" : value.slice(0, lastSlash);
  const fragment = lastSlash === -1 ? value : value.slice(lastSlash + 1);
  const segments = rawBase.split("/").filter(Boolean);
  return { base: segments.join("/"), fragment, index: segments.length };
};

/** Concrete document paths matching the whole typed value (prefix first). */
export const curatedDocumentMatches = (value: string, max = 6): string[] => {
  const needle = normalizePath(value).toLowerCase();
  if (needle === "") {
    return CURATED_DOCUMENTS.slice(0, max);
  }
  const matches = CURATED_DOCUMENTS.filter((path) =>
    path.toLowerCase().includes(needle),
  );
  matches.sort((a, b) => {
    const aStarts = a.toLowerCase().startsWith(needle) ? 0 : 1;
    const bStarts = b.toLowerCase().startsWith(needle) ? 0 : 1;
    return aStarts - bStarts || a.localeCompare(b);
  });
  return matches.slice(0, max);
};

/** Literal next-segment names under `base` whose value startsWith `fragment`. */
export const curatedNextSegments = (
  base: string,
  fragment: string,
  index: number,
  max = 8,
): string[] => {
  const prefix = base ? `${base}/` : "";
  const needle = fragment.toLowerCase();
  const found = new Set<string>();
  for (const path of CURATED_PATHS) {
    if (base && !path.startsWith(prefix)) {
      continue;
    }
    const segment = path.split("/")[index];
    if (!segment || segment.startsWith(":")) {
      continue;
    }
    if (segment.toLowerCase().startsWith(needle)) {
      found.add(segment);
    }
  }
  return Array.from(found).sort().slice(0, max);
};
